import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Briefcase, Plus } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Empty, PageHeader, Spinner, StatusBadge } from "@/components/kit";
import { firstName, inr, num, pct } from "@/lib/format";

const TABS = [
  { id: "stock", label: "Stocks" },
  { id: "commodity", label: "Commodities" },
  { id: "business", label: "Business" },
];

function Sides({ sides }) {
  return (
    <div className="flex gap-2 mt-3 flex-wrap">
      {sides.map((s) => (
        <span key={s.karta_id} className="hd-chip bg-[#F5F0E9] text-[#4A5568]">{firstName(s.name)} Side {pct(s.percentage)}</span>
      ))}
    </div>
  );
}

function HoldingCard({ h }) {
  const a = h.asset;
  return (
    <Link to={`/holding/${h.id}`} data-testid={`holding-card-${h.id}`} className="hd-card block hover:border-[#C8553D]/40 transition-colors fade-up">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-bold text-[16px] truncate">{a.name}</div>
          <div className="text-xs text-[#718096] mt-0.5 truncate">
            {h.category === "stock" && `${a.symbol} · ${a.exchange}`}
            {h.category === "commodity" && `Measured in ${a.unit}`}
            {h.category === "business" && `${a.legal_entity_name} · ${a.entity_type}`}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          {h.status !== "active" && <StatusBadge status={h.status} />}
          {h.pending_count > 0 && <span className="hd-chip bg-amber-50 text-amber-700">{h.pending_count} pending</span>}
        </div>
      </div>
      {h.category !== "business" ? (
        <div className="flex items-end justify-between mt-3">
          <div>
            <div className="text-xl font-bold" data-testid={`holding-qty-${h.id}`}>{num(h.quantity)} <span className="text-sm font-semibold text-[#718096]">{h.category === "stock" ? "Shares" : h.unit}</span></div>
            <div className="text-xs text-[#718096] mt-0.5">{h.open_lots} open lot{h.open_lots === 1 ? "" : "s"}</div>
          </div>
          <div className="text-right">
            <div className="text-xs text-[#718096]">{h.category === "stock" ? "Average cost" : `Avg rate / ${h.unit}`}</div>
            <div className="font-bold" data-testid={`holding-avg-${h.id}`}>{inr(h.avg_price)}</div>
          </div>
        </div>
      ) : (
        <div className="flex items-end justify-between mt-3">
          <div>
            <div className="text-xs text-[#718096]">Total invested</div>
            <div className="text-xl font-bold">{inr(h.invested)}</div>
          </div>
          {h.stake_pct != null && (
            <div className="text-right">
              <div className="text-xs text-[#718096]">Shareholding</div>
              <div className="font-bold">{pct(h.stake_pct)}</div>
            </div>
          )}
        </div>
      )}
      {h.ownership.sides.length > 0 && h.ownership.persons.length > 0 && <Sides sides={h.ownership.sides} />}
    </Link>
  );
}

export default function Holdings() {
  const { gid } = useAuth();
  const [sp, setSp] = useSearchParams();
  const tab = sp.get("tab") || "stock";
  const [items, setItems] = useState(null);
  const [showClosed, setShowClosed] = useState(false);

  useEffect(() => {
    setItems(null);
    api.get(`/groups/${gid}/holdings`, { params: { category: tab } }).then((r) => setItems(r.data));
  }, [gid, tab]);

  const visible = (items || []).filter((h) => showClosed || h.status !== "closed");
  const closedCount = (items || []).filter((h) => h.status === "closed").length;
  return (
    <div>
      <PageHeader title="Holdings" back={false}
        right={<Link to={`/add/${tab}`} data-testid="holdings-add-button" className="hd-btn-ghost h-10 px-3.5 text-sm"><Plus size={16} /> Add</Link>} />
      <div className="flex bg-white rounded-2xl p-1 border border-[#E2D9CF]/60" data-testid="holdings-tabs">
        {TABS.map((t) => (
          <button key={t.id} data-testid={`holdings-tab-${t.id}`} onClick={() => setSp({ tab: t.id })}
            className={`flex-1 h-10 rounded-xl text-sm font-semibold transition-colors ${tab === t.id ? "bg-[#C8553D] text-white" : "text-[#4A5568]"}`}>
            {t.label}
          </button>
        ))}
      </div>
      <div className="mt-4 space-y-3">
        {items === null ? <Spinner /> : visible.length === 0 ? (
          <Empty icon={Briefcase} text="Nothing here yet. Tap + to add your first entry." testid="holdings-empty" />
        ) : visible.map((h) => <HoldingCard key={h.id} h={h} />)}
      </div>
      {closedCount > 0 && (
        <button className="mt-4 text-sm font-semibold text-[#C8553D]" data-testid="holdings-toggle-closed" onClick={() => setShowClosed((v) => !v)}>
          {showClosed ? "Hide" : "Show"} {closedCount} closed
        </button>
      )}
    </div>
  );
}
